import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, FileJson, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const DataExport = () => {
  const [exporting, setExporting] = useState(false);

  const exportUserData = async () => {
    setExporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch all user data
      const [
        profileData,
        walletData,
        transactionsData,
        tipsData,
        subscriptionsData,
        streamsData,
        notificationPrefsData,
      ] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).single(),
        supabase.from('wallets').select('*').eq('user_id', user.id).single(),
        supabase.from('transactions').select('*').eq('user_id', user.id),
        supabase.from('tips').select('*').or(`from_user_id.eq.${user.id},to_user_id.eq.${user.id}`),
        supabase.from('subscriptions').select('*').eq('subscriber_id', user.id),
        supabase.from('streams').select('*').eq('creator_id', user.id),
        supabase.from('notification_preferences').select('*').eq('user_id', user.id).single(),
      ]);

      const userData = {
        export_date: new Date().toISOString(),
        user_id: user.id,
        email: user.email,
        profile: profileData.data,
        wallet: walletData.data,
        transactions: transactionsData.data,
        tips: tipsData.data,
        subscriptions: subscriptionsData.data,
        streams: streamsData.data,
        notification_preferences: notificationPrefsData.data,
      };

      // Create and download JSON file
      const dataStr = JSON.stringify(userData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `histage-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully', {
        description: 'Your personal data has been downloaded as a JSON file',
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data', {
        description: 'Please try again or contact support',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileJson className="h-5 w-5" />
          GDPR Data Export
        </CardTitle>
        <CardDescription>
          Download a complete copy of your personal data stored on HiStage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground space-y-2">
          <p>Your export will include:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Profile information and account details</li>
            <li>Wallet balance and transaction history</li>
            <li>Tips sent and received</li>
            <li>Subscription records</li>
            <li>Stream history (for creators)</li>
            <li>Notification preferences</li>
          </ul>
        </div>
        
        <Button 
          onClick={exportUserData} 
          disabled={exporting}
          className="w-full sm:w-auto"
        >
          {exporting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Export My Data
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground">
          This export complies with GDPR Article 20 (Right to Data Portability). 
          The file will be downloaded in JSON format.
        </p>
      </CardContent>
    </Card>
  );
};
